/**
 * The hero figure's light on the Buddy page's start (board P5e, variant D „Światło", owner,
 * 4 November 2026). The figure's shape never moves; the pearl's light answers the composer:
 *
 *   · waiting   the pearl brightens and dims once every 6 to 8 s (the pulse)
 *   · focus     one light sweep from the feet to the head, and the pearl stays lit
 *   · typing    the bright side follows the caret, and returns a second after the last input
 *
 * The state and the events that move it are here; the timers, the field's events and the caret's
 * place are `lib/useHeroLight.ts`'s; the drawing is `shared/ui/PearlMark`'s.
 */

export type HeroLightPhase = 'waiting' | 'focus' | 'typing';

export interface HeroLight {
  readonly phase: HeroLightPhase;
  readonly lit: boolean;
  /** The cone's start angle, in degrees: 0 square to the figure. */
  readonly angle: number;
  /** How many pulses and sweeps have been played: a new count plays one more. */
  readonly pulses: number;
  readonly sweeps: number;
}

export type HeroLightEvent =
  | { readonly kind: 'focus' }
  | { readonly kind: 'blur' }
  /** An input; `at` is the caret's place across the field's width, 0 at the left edge, 1 at the right. */
  | { readonly kind: 'input'; readonly at: number }
  /** A second passed since the last input. */
  | { readonly kind: 'settle' }
  /** The waiting pulse is due. */
  | { readonly kind: 'pulse' };

export const HERO_LIGHT_AT_REST: HeroLight = { phase: 'waiting', lit: false, angle: 0, pulses: 0, sweeps: 0 };

/** How far the bright side turns either way, in degrees, as the caret crosses the field. */
const TURN = 40;

/** How long after the last input the bright side returns. */
export const HERO_LIGHT_SETTLE_MS = 1_000;

/** When the first waiting pulse comes, and how long a pulse and the quiet after it last. */
export const HERO_LIGHT_FIRST_PULSE_MS = 1_200;
export const HERO_LIGHT_PULSE_MS = 3_600;
export const HERO_LIGHT_REST_MS = 2_400;
export const HERO_LIGHT_REST_SPREAD_MS = 2_000;

export function heroLight(state: HeroLight, event: HeroLightEvent): HeroLight {
  switch (event.kind) {
    case 'focus':
      return state.phase === 'waiting' ? lightUp(state) : state;
    case 'input': {
      const focused = state.phase === 'waiting' ? lightUp(state) : state;
      const at = Math.min(1, Math.max(0, event.at));
      return { ...focused, phase: 'typing', angle: -TURN + 2 * TURN * at };
    }
    case 'settle':
      return state.phase === 'typing' ? { ...state, phase: 'focus', angle: 0 } : state;
    case 'blur':
      return state.phase === 'waiting' ? state : { ...state, phase: 'waiting', lit: false, angle: 0 };
    case 'pulse':
      return state.phase === 'waiting' ? { ...state, pulses: state.pulses + 1 } : state;
  }
}

function lightUp(state: HeroLight): HeroLight {
  return { ...state, phase: 'focus', lit: true, angle: 0, sweeps: state.sweeps + 1 };
}
